import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { type PayOverTimeTransactionDetailsParams } from 'features/payOverTime/adapters/mapToTransactionSectionList';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import theme from 'shared/styles/theme';

const PayOverTimeTransactionDetails = () => {
  const { amount, description, date, status } =
    useLocalSearchParams<PayOverTimeTransactionDetailsParams>();

  const hasTransactionDetails = Boolean(
    amount || description || date || status,
  );

  const { bottom } = useSafeAreaInsets();

  const { t } = useTranslation('payOverTime');

  const dismissToTab = useDismissToRootTab();

  if (!hasTransactionDetails) {
    return (
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={t('loanDetails.error.title')}
        messageBody={t('loanDetails.error.body')}
        primaryButton={{
          title: t('loanDetails.error.button'),
          onPress: () => dismissToTab(Tabs.HOME),
        }}
      />
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: t('transactionDetails.header'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <ScrollView>
        <ExpressiveLgText style={styles.amountText}>{amount}</ExpressiveLgText>

        <View style={styles.detailsSection}>
          {description ? (
            <>
              <View style={styles.detailRow}>
                <BodyXSText style={styles.label}>
                  {t('transactionDetails.description')}
                </BodyXSText>
                <BodyBaseText>{description}</BodyBaseText>
              </View>
              {date || status ? (
                <View style={styles.hairlineDivider}>
                  <HairLineDivider />
                </View>
              ) : null}
            </>
          ) : null}

          {date ? (
            <>
              <View style={styles.detailRow}>
                <BodyXSText style={styles.label}>
                  {t('transactionDetails.requestedOn')}
                </BodyXSText>
                <BodyBaseText>{date}</BodyBaseText>
              </View>
              {status ? (
                <View style={styles.hairlineDivider}>
                  <HairLineDivider />
                </View>
              ) : null}
            </>
          ) : null}

          {status ? (
            <View style={styles.detailRow}>
              <BodyXSText style={styles.label}>
                {t('transactionDetails.status')}
              </BodyXSText>
              <BodyBaseText>{status}</BodyBaseText>
            </View>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  amountText: {
    alignSelf: 'center',
    marginVertical: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  detailRow: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  detailsSection: {
    paddingTop: theme.spacing.s,
  },
  label: {
    color: theme.color.text.secondary,
    marginBottom: 2,
  },
  hairlineDivider: {
    marginHorizontal: theme.spacing.m,
  },
});

export default PayOverTimeTransactionDetails;
